import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarDataHora } from '@/lib/formato';
import type { ContextoDoRetorno } from '@/lib/retorno';

import type { AjusteDaApi } from './api';
import { litrosComSinal, oQueEuPossoFazer, oQueOAjustePede, situacaoDoAjuste, valorDoAjuste } from './ajuste';
import { useAjustes, useAprovarAjuste, useCancelarAjuste, useRecusarAjuste } from './consultas';

/**
 * OS PEDIDOS DE AJUSTE — correção de lançamento e balanço do tanque (0059).
 *
 * Quem lançou errado ou conferiu o tanque PEDE; o livro só muda quando alguém
 * com alçada de ajuste (Cadastros › Governança) APROVA. Os pendentes vêm
 * primeiro, e os decididos ficam abaixo com quem decidiu e quando — é o
 * registro de aprovação que o dono pediu ao lado do histórico.
 *
 * Aprovar não é laranja: a tela já tem a ação laranja do hub, e decidir um
 * ajuste é exceção, não o caminho do dia. A janela de confirmação é que dá a
 * ação principal.
 */
/** Em quê e o que valeu, para o retorno (R04, p.32): o tanque e o que o pedido muda. */
function contextoDoAjuste(a: AjusteDaApi): ContextoDoRetorno {
  return { alvo: { codigo: a.tanqueCodigo, nome: a.tanqueNome }, fatos: [oQueOAjustePede(a), litrosComSinal(a.diferencaL)] };
}

export function PedidosDeAjuste() {
  const consulta = useAjustes();
  const aprovar = useAprovarAjuste();
  const recusar = useRecusarAjuste();
  const cancelar = useCancelarAjuste();

  const [aprovando, setAprovando] = useState<AjusteDaApi | null>(null);
  const [recusando, setRecusando] = useState<AjusteDaApi | null>(null);
  const [nota, setNota] = useState('');

  const ajustes = consulta.data ?? [];
  const paginaDosAjustes = usePaginacao(ajustes);
  const pendentes = ajustes.filter((a) => a.situacao === 'aguardando aprovacao').length;

  function fechar() {
    setAprovando(null);
    setRecusando(null);
    setNota('');
    aprovar.reset();
    recusar.reset();
  }

  const COLUNAS: readonly Coluna<AjusteDaApi>[] = [
    {
      chave: 'pedido',
      rotulo: 'Pedido',
      prioridade: 'chave',
      celula: (a) => (
        <>
          <TextoDaCelula texto={oQueOAjustePede(a)} classe="txt-corpo text-tinta" linhas={1} />
          <TextoDaCelula texto={`${a.tanqueCodigo} · ${a.motivo}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </>
      ),
    },
    {
      chave: 'diferenca',
      rotulo: 'Efeito no volume',
      alinhamento: 'numero',
      celula: (a) => (
        <>
          <span className="numero text-tinta">{litrosComSinal(a.diferencaL)}</span>
          <span className="txt-linha2 block text-tinta-secundaria">{valorDoAjuste(a)}</span>
        </>
      ),
    },
    {
      chave: 'pedidoPor',
      rotulo: 'Pedido por',
      celula: (a) => (
        <>
          <TextoDaCelula texto={a.solicitadoPor ?? '—'} classe="txt-corpo text-tinta" linhas={1} />
          <span className="txt-linha2 block text-tinta-secundaria">{formatarDataHora(new Date(a.solicitadoEm))}</span>
        </>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      /* Rótulo escrito, nunca só a cor (p.09). Quem decidiu e quando vão
         na segunda linha: é o registro de aprovação. */
      celula: (a) => (
        <>
          <span className="txt-corpo text-tinta">{situacaoDoAjuste(a.situacao)}</span>
          {a.decididoEm === null ? null : (
            <TextoDaCelula
              texto={`${a.situacao === 'cancelado' ? 'por quem pediu' : `por ${a.decididoPor ?? '—'}`} em ${formatarDataHora(new Date(a.decididoEm))}${a.notaDaDecisao ? ` · ${a.notaDaDecisao}` : ''}`}
              classe="txt-apoio text-tinta-apagada"
              linhas={1}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="txt-secao text-tinta">Pedidos de ajuste</h2>
        {/* "Nenhum esperando aprovação" antes da resposta afirmava o que
            ninguém sabia (p.32): a linha é esqueleto, e a tabela anuncia. */}
        {consulta.isPending ? (
          <Barra className="my-1 h-3 w-3/5" />
        ) : (
          <p className="text-tinta-secundaria">
            {pendentes === 0
              ? 'Correções de lançamento e balanços de tanque. Nenhum esperando aprovação agora.'
              : `Correções de lançamento e balanços de tanque. ${pendentes === 1 ? 'Um espera' : `${pendentes} esperam`} aprovação de quem tem alçada de ajuste.`}
          </p>
        )}
      </div>

      <Tabela
        rotulo="Pedidos de ajuste"
        colunas={COLUNAS}
        linhas={paginaDosAjustes.visiveis}
        rodape={<RodapeTabela {...paginaDosAjustes.rodape} />}
        chaveDaLinha={(a) => a.id}
        carregando={consulta.isPending}
        acoes={(a) => {
          const pode = oQueEuPossoFazer(a);
          if (pode.decidir) {
            return (
              <div className="flex items-center justify-end gap-2">
                <Botao nivel="terciario" onClick={() => { setAprovando(a); setNota(''); }}>Aprovar</Botao>
                <Botao nivel="terciario" onClick={() => { setRecusando(a); setNota(''); }}>Recusar</Botao>
              </div>
            );
          }
          if (pode.cancelar) {
            return (
              <div className="flex items-center justify-end gap-2">
                <span className="txt-apoio text-tinta-apagada">{pode.porQueNao}</span>
                <Botao nivel="terciario" disabled={cancelar.isPending} onClick={() => { cancelar.mutate({ id: a.id, contexto: contextoDoAjuste(a) }); }}>
                  Cancelar pedido
                </Botao>
              </div>
            );
          }
          return pode.porQueNao === null ? null : <span className="txt-apoio text-tinta-apagada">{pode.porQueNao}</span>;
        }}
        erro={
          consulta.isError ? (
            <EstadoVazio
              titulo="Não foi possível carregar os pedidos de ajuste"
              descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao nivel="secundario" onClick={() => { void consulta.refetch(); }}>
                  Carregar os pedidos
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          <EstadoVazio
            titulo="Nenhum pedido de ajuste"
            descricao="Quem lançou errado pede a correção pelos lançamentos do tanque, e quem conferiu o tanque pede o balanço pela linha dele. Os pedidos aparecem aqui até alguém com alçada de ajuste decidir."
          />
        }
      />

      <Janela
        titulo="Aprovar ajuste"
        aberta={aprovando !== null}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>Voltar aos pedidos</Botao>
            <Botao
              nivel="primario"
              disabled={aprovar.isPending}
              onClick={() => {
                if (aprovando === null) return;
                aprovar.mutate(
                  { id: aprovando.id, ...(nota.trim() ? { nota: nota.trim() } : {}), contexto: contextoDoAjuste(aprovando) },
                  { onSuccess: fechar },
                );
              }}
            >
              {aprovar.isPending ? 'Aprovando…' : 'Aprovar ajuste'}
            </Botao>
          </>
        }
      >
        {aprovando === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {aprovar.isError ? (
              <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(aprovar.error, 'A aprovação não foi aceita')}</p>
            ) : null}
            <p>
              {oQueOAjustePede(aprovando)} em {aprovando.tanqueNome}. O volume do tanque muda em{' '}
              <strong className="tabular-nums">{litrosComSinal(aprovando.diferencaL)}</strong>, e o volume ganha a correção
              com o seu nome e a hora da aprovação.
            </p>
            <p className="txt-apoio text-tinta-secundaria">Motivo de quem pediu: {aprovando.motivo}</p>
            <CampoTexto
              rotulo="Observação"
              opcional
              valor={nota}
              aoMudar={setNota}
              maxLength={300}
              placeholder="Conferi a nota fiscal 12345"
            />
          </div>
        )}
      </Janela>

      <Janela
        titulo="Recusar ajuste"
        aberta={recusando !== null}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>Voltar aos pedidos</Botao>
            <Botao
              nivel="destrutivo"
              disabled={nota.trim().length < 15 || recusar.isPending}
              onClick={() => {
                if (recusando === null) return;
                recusar.mutate({ id: recusando.id, nota: nota.trim(), contexto: contextoDoAjuste(recusando) }, { onSuccess: fechar });
              }}
            >
              {recusar.isPending ? 'Recusando…' : 'Recusar ajuste'}
            </Botao>
          </>
        }
      >
        {recusando === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {recusar.isError ? (
              <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(recusar.error, 'A recusa não foi aceita')}</p>
            ) : null}
            <p>
              {oQueOAjustePede(recusando)} em {recusando.tanqueNome}. Recusado, o volume não muda e quem pediu vê o motivo.
            </p>
            <CampoTexto
              rotulo="Por que o ajuste foi recusado"
              valor={nota}
              aoMudar={setNota}
              maxLength={300}
              placeholder="A nota fiscal confirma 19.000 L, o lançamento está certo"
              ajuda="Quem pediu precisa saber o que refazer."
              validar={(v) => (v.trim().length < 15 ? 'Escreva o motivo com pelo menos 15 caracteres.' : null)}
            />
          </div>
        )}
      </Janela>
    </section>
  );
}

/** A recusa do servidor já vem escrita para quem opera; a frase genérica só entra quando não veio nada. */
function mensagemDoErro(erro: unknown, padrao: string): string {
  const corpo = (erro as { corpo?: { erro?: string; comoResolver?: string } } | null)?.corpo;
  if (corpo?.comoResolver) return `${corpo.erro ?? padrao}. ${corpo.comoResolver}`;
  return `${padrao}: o pedido não chegou ao servidor. Tente outra vez; se continuar, avise o suporte com o horário.`;
}
